import { Box, Group, Text, useMantineColorScheme } from "@mantine/core";
import { IconLoader2 } from "@tabler/icons-react";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useMemo, useState } from "react";
import { useDatasources } from "src/hooks/useDatasources";
import type { ChatPreferences } from "../utils/filterDisplayUtils";
import { getDatasourceLabel } from "./ReferenceViewer";

export const ReadingIndicator = () => {
    const { colorScheme } = useMantineColorScheme();
    const { data: datasources = [] } = useDatasources();
    const [chatPreferences] = useLocalStorage<ChatPreferences>("chatPreferences", {
        selectedProductLines: [],
        selectedDatasources: [],
    });

    const [currentDatasourceIndex, setCurrentDatasourceIndex] = useState(0);

    // Get datasource names from backend datasources
    const datasourceNames = useMemo(() => {
        const selectedSources = chatPreferences.selectedDatasources || [];
        return selectedSources
            .map((sourceId: string) => getDatasourceLabel(sourceId, datasources) || sourceId)
            .filter(Boolean);
    }, [chatPreferences.selectedDatasources, datasources]);

    // Cycle through datasources randomly every 6 seconds
    useEffect(() => {
        if (datasourceNames.length > 0) {
            const interval = setInterval(() => {
                setCurrentDatasourceIndex((prev) => {
                    if (datasourceNames.length === 1) return 0;
                    let newIndex: number;
                    do {
                        newIndex = Math.floor(Math.random() * datasourceNames.length);
                    } while (newIndex === prev);
                    return newIndex;
                });
            }, 6000);
            return () => clearInterval(interval);
        }
        return () => {}; // Return empty cleanup function when no interval is set
    }, [datasourceNames.length]);

    return (
        <Box
            style={(theme) => ({
                marginBottom: theme.spacing.sm,
                padding: theme.spacing.sm,
                borderRadius: theme.radius.sm,
                backgroundColor:
                    colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
                border: `1px solid ${colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "2px",
                    background: "linear-gradient(90deg, transparent, #8A00E5, transparent)",
                    animation: "processing 2s ease-in-out infinite",
                },
                "@keyframes processing": {
                    "0%": { left: "-100%" },
                    "100%": { left: "100%" },
                },
            })}
        >
            <Group gap="xs" align="center">
                <IconLoader2 size={14} className="animate-spin" />
                <Text size="sm" fw={500} color="dimmed">
                    Reading:
                </Text>
                {datasourceNames.length > 0 ? (
                    <Box
                        style={{
                            position: "relative",
                            height: 24,
                            overflow: "hidden",
                            minWidth: 180,
                            flex: 1,
                        }}
                    >
                        {datasourceNames.map((name, index) => (
                            <Text
                                key={name}
                                size="sm"
                                fw={500}
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    transition: "transform 400ms ease-in-out, opacity 200ms ease",
                                    transform: `translateY(${(index - currentDatasourceIndex) * 24 + 1}px)`,
                                    opacity: index === currentDatasourceIndex ? 1 : 0,
                                }}
                            >
                                {name}
                            </Text>
                        ))}
                    </Box>
                ) : (
                    <Text size="sm" fw={500}>
                        documents
                    </Text>
                )}
            </Group>
        </Box>
    );
};
